/*
 * <anzu-grips> — the pinned grip flow for the Anzu A+ ergonomics panel.
 *
 * The panel holds while three states replace each other in place: the hand on the left
 * changes grip, the title and paragraph on the right change with it, and the counter steps
 * 1/3 → 2/3 → 3/3. The heading above never moves, which is what makes the three read as one
 * idea rather than three sections.
 *
 * ---- the interaction ----
 *
 * Advancing is a GESTURE, not a scroll depth. One nudge of the wheel or one swipe of a thumb —
 * however small, however hard thrown — moves the flow on by exactly one state, and the change
 * then runs on its own clock. Nothing here reads how far the page has scrolled to decide what to
 * draw, so a gentle nudge and a hard flick produce the same single step, and the reader never has
 * to hunt for the scroll position where a state is finally legible.
 *
 * Three things make that safe rather than a trap:
 *
 *  - It releases at both ends, and leaving is read as a gesture like any other. A nudge or swipe
 *    that would carry past the last state (or back before the first) is not consumed at all: the
 *    page scrolls, normally, on that very gesture. But it has to be its OWN gesture — the tail of
 *    the flick that ARRIVED at the last state cannot also be the thing that leaves, or the
 *    last grip is gone before it can be read. So no gesture is ever spent leaving; one is
 *    spent arriving, exactly as every other state costs one.
 *  - It parks the page on each state's share of the track. The stage is pinned, so moving
 *    the scroll position is invisible — but it keeps position and state telling the same
 *    story, which is what lets the release above happen the instant the flow is finished
 *    instead of after another screenful of scrolling.
 *  - It never runs against a stated motion preference.
 *
 * One gesture is one grip, however hard it is thrown. What COUNTS as one gesture is the only
 * place the two inputs differ, and only because one of them is worse at saying so. A touch has
 * edges: touchstart opens it, touchend closes it, and that is exact. A wheel has none, so a
 * gesture there ends when the wheel goes quiet rather than when the change finishes — a trackpad
 * reports one flick as a long stream of events and keeps trickling after it, and a wheel spun
 * without pause is one continuous throw. Both are one gesture, so both move the flow by one.
 *
 * Reduced motion or no JS: nothing pins, is-driven never lands, and the CSS leaves
 * the three states stacked as a plain readable list. The fallback is the default, not a
 * degraded copy of the driven layout.
 */
(function () {
  var TRIGGER = 10;  // accumulated |deltaY| that counts as a deliberate nudge
  // Long enough to outlast a momentum tail, which keeps trickling well after the flick is
  // over. Shorter and the dying end of one throw re-arms and spends a second grip.
  var IDLE = 200;    // ms the wheel must go quiet before a new gesture counts
  /* Leaving normally costs a fresh gesture, which means quiet. An unbroken scroll never goes
     quiet, so this is the escape: at either end of the flow, a scroll that simply never stops
     is let through once the last state has had this long on screen. Longer than a momentum
     tail, which decays out inside about a second — so a flick still cannot leave on the tail
     of its own arrival — and short enough that a reader dragging steadily is held for a beat
     rather than stuck until they lift their fingers. */
  var HOLD = 1200;
  var FALLBACK_MS = 520;

  /* ---- the same restriction, by touch ----
     A swipe is a whole gesture with edges the wheel does not have: touchstart opens it and
     touchend closes it. So "one gesture, one grip" needs no quiet period here and no threshold
     guessing — the IDLE/HOLD machinery above exists only because a wheel never tells you when a
     flick is over. One touch sequence spends exactly one grip, and the next one is the next
     gesture. That is the desktop rule, stated more exactly than the desktop can state it.

     TOUCH_TRIGGER is how far a thumb travels before the flow takes it as deliberate: far enough
     that resting a finger and drifting does nothing, short enough that the grip changes early in
     the swipe rather than at the end of it. TOUCH_DIR is smaller and does a different job — it is
     how far the finger must go before its DIRECTION is worth reading, and it has to stay under
     the browser's own scroll slop (about 8px on Android and iOS both). Past the slop the browser
     has already begun scrolling and preventDefault is ignored for the rest of the sequence, so
     every decision this file makes about a swipe has to be made inside those first few pixels. */
  var TOUCH_TRIGGER = 44;
  var TOUCH_DIR = 5;

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  var FIELD = /^(INPUT|TEXTAREA|SELECT|BUTTON|A)$/;

  class AnzuGrips extends HTMLElement {
    connectedCallback() {
      if (this.dataset.gripsReady) return;
      this.dataset.gripsReady = '1';

      this.stage = this.querySelector('[data-grips-stage]');
      this.steps = [].slice.call(this.querySelectorAll('[data-grips-step]'));
      this.index = this.querySelector('[data-grips-index]');
      if (!this.stage || this.steps.length < 2) return;

      this.section = this.closest('.aplus-grips');
      // Held rather than re-queried: pinned() runs on every wheel event, and matchMedia()
      // builds a new object each call.
      this.narrow = window.matchMedia ? matchMedia('(max-width: 749px)') : null;
      this.still = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : null;
      /* Narrow says how the section is DRAWN; coarse says what the reader is holding. Both are
         needed, and for opposite reasons: the swipe restriction below should reach a phone and a
         device-emulated phone, and should not reach a desktop window someone has dragged narrow,
         where the scrollbar is a real input and being pulled back to an anchor while dragging it
         would read as the page fighting them. */
      this.coarse = window.matchMedia ? matchMedia('(pointer: coarse)') : null;
      // Set the first time a finger actually lands. Belt to the media query's braces: a
      // touchscreen laptop in a narrow window reports a fine pointer and still swipes.
      this.sawTouch = false;

      this.step = 0;
      this.acc = 0;
      this.lastWheelAt = 0;
      this.doneAt = 0;
      this.settling = false;
      // Set once the flow has handed a gesture back at one of its ends; cleared the moment the
      // flow moves again. Kept on the element rather than worked out from the step, because
      // "we are at the last grip" and "we have already let go" are different facts.
      this.freed = false;
      this.endAt = 0;
      this.inside = false;
      this.above = true;
      this.head = 0;

      // Per-swipe, all reset on touchstart. tOwned is deliberately three-valued: null means the
      // finger has not moved far enough to say whose gesture this is yet, and that undecided
      // state is the whole of the touch logic.
      this.tY = null;
      this.tAcc = 0;
      this.tSpent = false;
      this.tOwned = null;

      this.onWheel = this.wheel.bind(this);
      this.onKey = this.key.bind(this);
      this.onScroll = this.scrolled.bind(this);
      this.onResize = this.resize.bind(this);
      this.onTouchStart = this.touchStart.bind(this);
      this.onTouchMove = this.touchMove.bind(this);
      this.onTouchEnd = this.touchEnd.bind(this);

      // Not passive: consuming the gesture is the whole point.
      window.addEventListener('wheel', this.onWheel, { passive: false });
      window.addEventListener('touchstart', this.onTouchStart, { passive: true });
      // Nor this one, and for the same reason. A touchmove listener is passive by default at the
      // document level in every current browser, so the flag is not optional here.
      window.addEventListener('touchmove', this.onTouchMove, { passive: false });
      window.addEventListener('touchend', this.onTouchEnd, { passive: true });
      window.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
      window.addEventListener('keydown', this.onKey);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      /* The header is not a fixed quantity. theme.js republishes --sticky-header-height the moment
         the page is scrolled past it, and the header condenses when it does — so a height measured
         once, at load, is the TALL one, and everything derived from it is out by the difference
         for the whole rest of the page.

         That lands entirely on the top end of this flow. The last grip is parked in the middle of
         the band, where a few tens of pixels of drift are absorbed; the FIRST grip is parked on
         pinStart, which IS the band's edge, so the same drift puts the reader outside it. And
         outside the band the wheel handler hands the event straight to the page — no hold, no
         gesture, nothing. Which is exactly "it waits at the bottom and not at the top".

         The theme announces the change, so the fix is to listen rather than to re-measure on every
         wheel event, which would cost a style resolve per event. */
      document.addEventListener('header:scrolled', this.onResize);
      for (var i = 0, m = [this.narrow, this.still]; i < m.length; i++) {
        if (m[i] && m[i].addEventListener) m[i].addEventListener('change', this.onResize);
      }

      this.resize();
    }

    disconnectedCallback() {
      window.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('touchstart', this.onTouchStart);
      window.removeEventListener('touchmove', this.onTouchMove);
      window.removeEventListener('touchend', this.onTouchEnd);
      window.removeEventListener('touchcancel', this.onTouchEnd);
      window.removeEventListener('keydown', this.onKey);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('header:scrolled', this.onResize);
      for (var i = 0, m = [this.narrow, this.still]; i < m.length; i++) {
        if (m[i] && m[i].removeEventListener) m[i].removeEventListener('change', this.onResize);
      }
      if (this.raf) cancelAnimationFrame(this.raf);
    }

    // Pinning is opt-in per section and never runs against a stated motion preference. The
    // CSS unpins under exactly the same conditions, so the two can't disagree.
    pinned() {
      if (this.still && this.still.matches) return false;
      if (this.narrow && this.narrow.matches) return this.dataset.gripsPinMobile === 'true';
      return this.dataset.gripsPinDesktop === 'true';
    }

    /* A phone runs the same flow, under the same restriction, off a different input.
       Touching a screen fires no wheel events at all, so the gesture model below reads something
       that is not there. The phone used to answer that by driving the flow from scroll POSITION —
       the panel scrubbed by the thumb rather than stepped by swipes — which kept the three states
       but gave up the restriction that makes them readable: no state was held, and the flow was
       gone in whatever fraction of a fling crossed the band.

       So the input is read directly here instead. The old objection to that was fair — reading
       touchmove and calling preventDefault is how a page ends up fighting its own fling — and it
       is answered rather than ignored: a gesture the flow consumes never becomes a fling, because
       the browser only launches momentum from a scroll it was allowed to start. There is nothing
       to fight. What can still arrive under momentum is a fling begun ABOVE the section, and that
       is caught once, at the band's edge, by the anchor hold in scrolled().

       Both signals have to agree. Narrow alone would put a desktop window dragged narrow into the
       same hold, where the scrollbar is a real input and being pulled back to an anchor mid-drag
       is the page fighting the reader for real. Coarse alone would miss nothing in practice but
       costs nothing to pair. */
    swipeDriven() {
      if (!(this.narrow && this.narrow.matches)) return false;
      return this.sawTouch || !!(this.coarse && this.coarse.matches);
    }

    last() { return this.steps.length - 1; }

    // One source of truth for the tempo: the CSS owns it, the input lock reads it back.
    duration() {
      if (!this.section) return FALLBACK_MS;
      var v = parseFloat(getComputedStyle(this.section).getPropertyValue('--grips-ms'));
      return v > 0 ? v : FALLBACK_MS;
    }

    // The stage rests at the header's line, not at the top of the window.
    syncHead() {
      var cs = getComputedStyle(document.documentElement);
      this.head = parseFloat(cs.getPropertyValue('--sticky-header-height')) || 0;
    }

    // Where the pin engages, and the run of scroll over which it holds.
    pinStart() { return this.getBoundingClientRect().top + window.scrollY - this.head; }
    travel() { return Math.max(0, this.offsetHeight - this.stageH); }
    anchor(i) { return this.pinStart() + (this.travel() * i) / this.last(); }

    atRest() {
      var y = window.scrollY;
      var s = this.pinStart();
      return y >= s - 1 && y <= s + this.travel() + 1;
    }

    stepFromScroll() {
      var t = this.travel();
      if (!t) return 0;
      return Math.round(clamp((window.scrollY - this.pinStart()) / t, 0, 1) * this.last());
    }

    // The track is only a scroll budget — for the scrollbar, and for readers who drag or use
    // touch rather than gesturing. Sized here because the stage is viewport-height, which CSS
    // alone cannot add to a percentage of the viewport in the same declaration.
    resize() {
      var on = this.pinned();
      if (this.section) this.section.classList.toggle('is-driven', on);
      if (!on) {
        this.style.height = '';
        if (this.section) this.section.classList.remove('is-ready');
        for (var i = 0; i < this.steps.length; i++) {
          this.steps[i].classList.remove('is-current', 'is-before');
        }
        if (this.index) this.index.textContent = '1';
        this.step = 0;
        this.inside = false;
        return;
      }
      this.syncHead();
      var advance = parseFloat(this.dataset.gripsAdvance) || 0;
      this.stageH = this.stage.offsetHeight;
      this.style.height = Math.round(this.stageH + (advance / 100) * window.innerHeight) + 'px';
      this.above = window.scrollY < this.pinStart();
      this.inside = this.atRest();
      /* Where the flow should be standing after a re-measure. Inside the band that is whatever
         the input says: the position, where the position is an input, and the step itself where
         swipes own it — reading it back off the scroll on a phone would let a re-measure undo the
         swipe that put it there. Outside, it is the end the reader is on.

         render() rather than show() when nothing moved, so a resize cannot re-arm a hold the
         reader has already released — and so the freed test on the park below is asked about the
         reader's own state rather than about one this line has just cleared. */
      var want = this.inside
        ? (this.swipeDriven() ? this.step : this.stepFromScroll())
        : (this.above ? 0 : this.last());
      if (want === this.step) this.render(want);
      else this.show(want);
      /* Re-park. Re-measuring moves the band under a reader who is standing in it, and a state
         shown at a position that no longer anchors it is the same bug one step later: the next
         scroll reads the position back, gets a different grip, and the panel jumps.

         This used to be skipped on a phone, because the position WAS the state there and
         correcting it would have overwritten the reader's own place in the flow. Swipes drive it
         now, so the position is a consequence rather than an input and re-parking is simply
         correct — which matters more on a phone than anywhere, since the address bar resizes the
         window every time it hides and takes the band's anchors with it.

         Not while the flow has been let go, though, on any device. A resize during the scroll
         that LEAVES the section would otherwise haul the reader back into it, and on a phone
         that scroll is exactly what makes the address bar move. */
      if (this.inside && !this.freed) this.park(Math.round(this.anchor(this.step)));

      /* The states snap into place on the first frame; only then do the transitions arm. Arm
         them together and the two states this one replaces would visibly fade out on load. */
      var self = this;
      requestAnimationFrame(function () {
        if (self.section && self.pinned()) self.section.classList.add('is-ready');
      });
    }

    /* Drawing a state, with nothing said about how the flow got there. Split out from show()
       because a RE-MEASURE is not a change of state: a resize has to be able to put the classes
       back without also telling the flow it has moved, or every address-bar hide would re-arm a
       hold the reader had already released. */
    render(i) {
      i = clamp(i, 0, this.last());
      this.step = i;
      for (var k = 0; k < this.steps.length; k++) {
        this.steps[k].classList.toggle('is-current', k === i);
        // Which way a state travels comes from its place in the flow, not from the direction
        // of the gesture, so going back reverses on its own.
        this.steps[k].classList.toggle('is-before', k < i);
      }
      if (this.index) this.index.textContent = String(i + 1);
    }

    /* Arriving at a state. Any arrival puts the flow back in charge, whichever end it was let go
       at, and restarts the beat this state is owed before an unbroken scroll may carry past it.
       Unconditional, and it has to be: re-entering the section at the same end you left it by is
       an arrival, so re-arming only on a CHANGE of step would let the flow be released again on
       the first gesture back in. */
    show(i) {
      this.render(i);
      this.freed = false;
      this.endAt = performance.now();
    }

    /* Move to a state AND park the page on that state's share of the track. Skipping the park
       would leave the scroll position saying "state 1" while the panel showed state 3 — so the
       first gesture past the end would scroll a few pixels, be read back as state 1, and throw
       the reader to the start. The park itself is invisible: the stage is pinned, so nothing
       on screen moves when the scroll position does. */
    go(i) {
      if (i === this.step || i < 0 || i > this.last()) return;
      this.show(i);
      this.park(Math.round(this.anchor(i)));
    }

    /* The park has to be instant. Left to animate — which it would be the moment anything
       sets scroll-behavior: smooth on the root — it would walk the page through every
       position between two states, and the scroll handler would read each one back and flick
       the panel to whatever grip that position claimed. */
    park(y) {
      var root = document.documentElement;
      var was = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(window.scrollX, y);
      root.style.scrollBehavior = was;
    }

    // Both ends hand the gesture straight back to the page, so the flow can never trap.
    spent(dir) {
      return (dir > 0 && this.step === this.last()) || (dir < 0 && this.step === 0);
    }

    /* Crossing into or out of the band. Both handlers call this, and the wheel handler must
       not wait for the scroll handler's frame to do it: a fast trackpad fires several events
       between frames, and the flick that carried the reader in would be read as a gesture and
       spend a grip on arrival. Returns true only on the crossing itself. */
    sync() {
      var rest = this.atRest();
      if (!rest) this.above = window.scrollY < this.pinStart();
      if (rest === this.inside) return false;
      this.inside = rest;
      if (rest) {
        this.settling = true;   // the flick that carried them here is not a gesture
        this.acc = 0;
      }
      // Entering starts the flow at the end the reader came in from; leaving parks it there.
      this.show(this.above ? 0 : this.last());
      return true;
    }

    wheel(e) {
      if (!this.pinned()) return;

      /* Timed on every event, consumed or not. A flick that begins above the panel is still
         the same flick when it arrives, and measuring the gap only from events we took would
         make the arrival look like a fresh gesture. */
      var now = performance.now();
      var gap = now - this.lastWheelAt;
      this.lastWheelAt = now;

      this.sync();
      if (!this.inside) return;

      var dy = e.deltaY;
      if (e.deltaMode === 1) dy *= 16;                  // lines
      else if (e.deltaMode === 2) dy *= this.stageH;    // pages
      if (!dy) return;
      var dir = dy > 0 ? 1 : -1;

      /* The flow is over in this direction and a gesture has already been handed back: the
         page owns the wheel from here, and nothing below runs again until the flow moves. */
      if (this.spent(dir) && this.freed) {
        this.acc = 0;
        this.settling = false;
        return;
      }

      /* Read the gesture BEFORE deciding whether to consume it. Leaving is a gesture like any
         other, and that is the whole of it: pointing the right way used to be enough, so the
         tail of the very flick that arrived at the last grip carried straight on into the
         page and the grip was gone before anyone could read it. Now the flick that arrives
         ends at the last grip, and the next one — after the wheel has gone quiet — is the one
         that moves the page. */
      var fire = false;
      if (now < this.doneAt) {
        this.acc = 0;                                   // mid-change: swallow the burst
      } else if (this.settling && gap < IDLE) {
        /* Still the same gesture that just spent a grip. A gesture ends when the wheel goes
           quiet, not when the change finishes, so a flick's momentum tail and a wheel spun
           without pause are both one gesture and can spend only one grip.

           An earlier version let a large delta through on the grounds that a mouse notch is
           already discrete and a spinning reader should not be held. That is the bug: the top
           of a hard trackpad flick reports deltas in the same range as a mouse notch, so a
           deep scroll re-armed mid-flick and went 1 to 3 in one throw. No threshold separates
           the two reliably — the quiet is the only honest signal. */
        this.acc = 0;
      } else {
        this.settling = false;
        if ((this.acc > 0) !== (dy > 0)) this.acc = 0;  // direction flipped mid-gesture
        this.acc += dy;
        fire = Math.abs(this.acc) >= TRIGGER;
      }

      /* And leaving is the one gesture that is not consumed. Letting THIS event keep its
         default is what starts the page on the same wheel event that ends the flow, rather
         than a frame later; everything after it is handed over by the branch at the top.

         The second clause is the escape hatch, and it is why holding the end is a beat and
         never a trap: a scroll that never goes quiet can never form a second gesture, so
         without it an unbroken drag would be held at the last grip until the reader lifted
         their fingers. */
      if (this.spent(dir) && (fire || now - this.endAt > HOLD)) {
        this.acc = 0;
        this.freed = true;
        return;
      }

      e.preventDefault();
      if (!fire) return;

      this.acc = 0;
      this.settling = true;
      this.doneAt = now + this.duration();
      this.go(this.step + dir);
    }

    /* ---- the swipe ------------------------------------------------------------------------
       One touch sequence, one grip — and the sequence, not a timer, is what says so.

       Everything hangs on a constraint the wheel does not have: once the browser has begun
       scrolling a touch, preventDefault on the rest of that sequence is ignored. So the question
       "is this swipe mine or the page's?" can only be answered inside the first few pixels, and
       it is answered exactly once per sequence, in tOwned. After that the answer stands, which is
       also what makes the arriving swipe unable to leave: it is already mine, to its end. */
    touchStart(e) {
      this.sawTouch = true;
      this.tY = null;
      this.tAcc = 0;
      this.tSpent = false;
      // A second finger is a pinch or a two-finger scroll, and neither is this flow's to take.
      this.tOwned = (e.touches && e.touches.length > 1) ? false : null;
      if (this.tOwned === false) return;
      var t = e.touches && e.touches[0];
      if (t) this.tY = t.clientY;
    }

    /* Clearing tY is the whole of it, and clearing tOwned as well would be a line no test can
       reach: every path into touchMove is guarded on tY first, and touchstart resets both before
       the next sequence can be read. */
    touchEnd() { this.tY = null; }

    touchMove(e) {
      if (this.tOwned === false || this.tY === null) return;
      if (!this.pinned() || !this.swipeDriven()) return;
      var t = e.touches && e.touches[0];
      if (!t) return;

      // Finger travelling UP is the page going down, which is the wheel's positive delta.
      this.tAcc += this.tY - t.clientY;
      this.tY = t.clientY;

      this.sync();
      if (!this.inside) { this.tOwned = false; return; }

      if (this.tOwned === null) {
        /* Undecided. Too small to read a direction from yet — and reading one from a pixel of
           jitter is how a swipe meant to leave the flow steps back into it instead. */
        if (Math.abs(this.tAcc) < TOUCH_DIR) return;
        var d0 = this.tAcc > 0 ? 1 : -1;
        /* The flow is over this way, so this whole swipe belongs to the page — never touched,
           so the browser can still start its scroll and carry its own momentum out of the
           section. The swipe that ARRIVED at this state cannot be this one: that one was
           claimed at its own first few pixels and stays claimed until the finger lifts. Which
           is the desktop rule — one gesture arrives, the next one leaves — with a touchend
           where the desktop can only estimate quiet. */
        if (this.spent(d0)) { this.freed = true; this.tOwned = false; return; }
        this.tOwned = true;
      }

      // Mine. Hold the page still whether or not this event is the one that moves the flow.
      if (e.cancelable) e.preventDefault();
      if (this.tSpent) return;
      if (Math.abs(this.tAcc) < TOUCH_TRIGGER) return;

      var dir = this.tAcc > 0 ? 1 : -1;
      this.tSpent = true;
      this.tAcc = 0;
      this.doneAt = performance.now() + this.duration();
      this.go(this.step + dir);
    }

    /* Keys move the flow exactly as the wheel does. Left to the page, a keyboard reader would
       have to push through the whole track a line at a time to reach the second state. */
    key(e) {
      if (!this.pinned() || !this.atRest()) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      var t = e.target;
      if (t && (t.isContentEditable || FIELD.test(t.tagName || ''))) return;

      var dir = 0;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') dir = 1;
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') dir = -1;
      else if (e.key === ' ' || e.key === 'Spacebar') dir = e.shiftKey ? -1 : 1;
      if (!dir || this.spent(dir)) return;

      e.preventDefault();
      var now = performance.now();
      if (now < this.doneAt) return;
      this.doneAt = now + this.duration();
      this.go(this.step + dir);
    }

    scrolled() {
      if (!this.pinned() || this.raf) return;
      var self = this;
      this.raf = requestAnimationFrame(function () {
        self.raf = 0;
        if (!self.pinned()) return;
        self.syncHead();

        /* A jump — an anchor link, Home/End, a hard drag of the scrollbar — can land most of
           the way through the track in one go, and reading the state off where it landed
           would skip straight past the grips in between. sync() starts the flow at the end
           the reader came in from; a drag that keeps going corrects itself on the next frame,
           through the branch below. */
        if (self.sync() || !self.inside) return;

        /* Under a thumb the position is not an input, so it does not get to choose the state —
           it gets held at the one the swipes have chosen. This is the only thing standing between
           the flow and a fling begun ABOVE the section: that fling arrives carrying momentum no
           touchmove can refuse, and without this it would scrub the whole flow past in the third
           of a second it takes to cross the band. Parking cancels momentum, so it is caught once,
           at whichever end it came in from, and the reader swipes from there.

           Never while the flow has been let go — that scroll is the reader leaving, and pulling
           them back would be the trap this whole file is written to avoid. */
        if (self.swipeDriven()) {
          if (self.freed) return;
          var home = Math.round(self.anchor(self.step));
          if (Math.abs(window.scrollY - home) > 1) self.park(home);
          return;
        }

        /* Moved inside the band by something other than a gesture — a scrollbar drag, a touch
           drag. Our own parking lands exactly on an anchor, so it reads back as the state we
           just set and falls out here as a no-op. */
        var want = self.stepFromScroll();
        if (want !== self.step) self.show(want);
      });
    }
  }

  if (!customElements.get('anzu-grips')) {
    customElements.define('anzu-grips', AnzuGrips);
  }

  window.theme = window.theme || {};
  window.theme.AnzuGrips = AnzuGrips;
})();
